// Shared Dougs draft snippet + clipboard helper. Used by the generation
// review table and the invoices history table (re-copy a lost snippet).

// Self-contained snippet run in the app.dougs.fr console (where the bound
// session lives). Creates + fills one draft per payload, then reports a summary.
// Accepts an ARRAY of payloads → single invoice = array of 1, bulk = all.
// The admin server cannot call Dougs itself (session is device/IP-bound), so
// draft creation runs in the browser.
export function buildDougsSnippet(payloads) {
	return `(async () => {
  const PAYLOADS = ${JSON.stringify(payloads)};
  const m = location.pathname.match(/\\/c\\/(\\d+)/);
  const CID = m ? m[1] : null;
  if (!CID) { console.error("[Dougs] Ouvrez d'abord un onglet app.dougs.fr/app/c/<id>/..."); return; }
  const BASE = location.origin + "/companies/" + CID;
  const api = async (method, path, body) => {
    const res = await fetch(BASE + path, { method, credentials: "include", headers: { "content-type": "application/json", accept: "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
    const t = await res.text(); let d; try { d = t ? JSON.parse(t) : null; } catch (e) { d = t; }
    if (!res.ok) throw new Error("[Dougs] " + method + " " + path + " -> " + res.status + "\\n" + t.slice(0, 300));
    return d;
  };
  const r2 = (n) => Math.round(n * 100) / 100;
  const vatFromSiren = (s) => { s = String(s).replace(/\\D/g, ""); if (s.length !== 9) return null; return "FR" + String((12 + 3 * (Number(s) % 97)) % 97).padStart(2, "0") + s; };
  const buildLine = (l) => { const vr = l.vatRate == null ? 0.2 : l.vatRate; const dc = l.discount || 0; return { title: l.title, description: null, reference: "", unit: l.unit || "unité", quantity: l.quantity, unitAmount: l.unitAmount, vatRate: vr, discount: dc, discountUnit: "%", discountInEuros: 0, amount: r2(l.quantity * l.unitAmount * (1 - dc / 100)), isPriceWithVat: false }; };
  const totals = (lines) => { const byR = {}; let net = 0, vat = 0; for (const ln of lines) { net += ln.amount; const v = r2(ln.amount * ln.vatRate); vat += v; byR[ln.vatRate] = byR[ln.vatRate] || { vatRate: ln.vatRate, amount: 0, vatAmount: 0 }; byR[ln.vatRate].amount = r2(byR[ln.vatRate].amount + ln.amount); byR[ln.vatRate].vatAmount = r2(byR[ln.vatRate].vatAmount + v); } net = r2(net); vat = r2(vat); return { totalAmount: net, totalNetAmount: net, totalVatAmount: vat, totalAmountWithVat: r2(net + vat), totalDiscountAmount: 0, depositAmount: 0, vatSummary: Object.values(byR), metadata: { hasGlobalVatRate: new Set(lines.map((l) => l.vatRate)).size <= 1 } }; };

  const createOne = async (PAYLOAD) => {
    const draft = await api("POST", "/sales-invoices-drafts", {});
    let clientData = draft.clientData;
    if (PAYLOAD.client) {
      const cfg = PAYLOAD.client;
      const addr = cfg.address || {};
      // If a SIRET is set but name/city/zip are missing, resolve them from Dougs
      // (Pappers). Street is kept from config — Dougs does not return it.
      const needResolve = cfg.siret && (!cfg.legalName || !addr.city || !addr.zipCode);
      let base = {};
      if (needResolve) {
        const found = await api("GET", "/sales-invoices-drafts/clients?isBtoB=true&name=" + encodeURIComponent(cfg.siret)).catch(() => []);
        const c = (found || []).find((x) => x.siren === String(cfg.siret).slice(0, 9)) || (found || [])[0];
        if (c) base = { legalName: c.legalName, firstName: c.firstName, lastName: c.lastName, siren: c.siren, vatNumber: c.vatNumber || (c.siren ? vatFromSiren(c.siren) : null), address: { city: (c.address && c.address.city) || "", zipCode: (c.address && (c.address.zipCode || c.address.zipcode)) || "" } };
        else console.warn("[Dougs] SIRET " + cfg.siret + " introuvable côté Dougs — données config utilisées telles quelles.");
      }
      const mergedAddress = Object.assign({ country: "France" }, base.address || {}, addr);
      clientData = Object.assign({}, clientData, base, cfg, { address: mergedAddress, isBToB: true });
    }
    else if (PAYLOAD.clientSearch) {
      const found = await api("GET", "/sales-invoices-drafts/clients?isBtoB=true&name=" + encodeURIComponent(PAYLOAD.clientSearch));
      if (!found.length) { console.warn("[Dougs] Client introuvable: " + PAYLOAD.clientSearch + " — brouillon vide créé (" + draft.id + "), à compléter manuellement."); }
      else {
        const c = PAYLOAD.clientSiren ? (found.find((x) => x.siren === PAYLOAD.clientSiren) || found[0]) : found[0];
        if (found.length > 1 && !PAYLOAD.clientSiren) console.warn("[Dougs] " + found.length + " clients pour '" + PAYLOAD.clientSearch + "', 1er utilisé:", found.map((x) => ({ nom: x.legalName, siren: x.siren, ville: x.address && x.address.city })));
        clientData = { siren: c.siren, siret: PAYLOAD.clientSiret || c.siret || "", legalName: c.legalName, lastName: c.lastName, firstName: c.firstName, address: { street: (c.address && c.address.street) || "", zipCode: (c.address && (c.address.zipCode || c.address.zipcode)) || "", city: (c.address && c.address.city) || "", country: (c.address && c.address.country) || "France" }, others: [], email: c.email || "", phone: c.phone || "", vatNumber: c.vatNumber || (c.siren ? vatFromSiren(c.siren) : null), deliveryAddress: null, isBToB: true, clientId: c.clientId };
      }
    }
    const lines = (PAYLOAD.lines || []).map(buildLine);
    const body = Object.assign({}, draft, { subject: PAYLOAD.subject || draft.subject, lines: lines, clientData: clientData }, totals(lines));
    const filled = await api("POST", "/sales-invoices-drafts/" + draft.id, body);
    const cf = await api("GET", "/sales-invoices-drafts/" + draft.id + "/actions/can-finalize");
    return { filled: filled, ready: !(Array.isArray(cf) && cf.length), missing: cf };
  };

  let ok = 0, fail = 0;
  console.log("%c[Dougs] Création de " + PAYLOADS.length + " brouillon(s)…", "color:#2e7d32;font-weight:bold");
  for (const P of PAYLOADS) {
    const label = P.clientSearch || (P.client && P.client.legalName) || "?";
    try {
      const r = await createOne(P);
      console.log("%c ✓ " + label + " — " + r.filled.totalNetAmount + " € HT / " + r.filled.totalAmountWithVat + " € TTC" + (r.ready ? "" : " (à compléter)"), "color:#2e7d32");
      if (!r.ready) console.warn("   → manquant:", r.missing);
      ok++;
    } catch (e) { console.error(" ✗ " + label + " : " + e.message); fail++; }
  }
  console.log("%c[Dougs] Terminé : " + ok + " créé(s), " + fail + " échec(s). Vérifiez puis finalisez dans Dougs.", "color:#2e7d32;font-weight:bold");
  console.log("[Dougs] Ouvrir: " + location.origin + "/app/c/" + CID + "/invoicing/sales-invoice?status=draft");
})();`;
}

export function copyToClipboard(text) {
	if (navigator.clipboard && navigator.clipboard.writeText) {
		return navigator.clipboard.writeText(text);
	}
	return new Promise((resolve, reject) => {
		try {
			const ta = document.createElement("textarea");
			ta.value = text;
			ta.style.position = "fixed";
			ta.style.opacity = "0";
			document.body.appendChild(ta);
			ta.select();
			document.execCommand("copy");
			document.body.removeChild(ta);
			resolve();
		} catch (e) {
			reject(e);
		}
	});
}
